import { useCallback, useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { DirectorySearchResult } from "@/services/directory-search-service"

import { Search, X } from "@/components/icons"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { DirectorySearchResultItem } from "./directory-search-result-item"
import { useDirectorySearch } from "./use-directory-search"

type DirectorySearchProps = {
  /** Callback when a result is selected */
  onSelect: (result: DirectorySearchResult) => void
  /** Currently selected OIDs (for multi-select mode) */
  selectedOids?: string[]
  /** Entity types to search. If not provided, uses config to determine available types */
  types?: ("users" | "groups")[]
  /** Placeholder text for the search input */
  placeholder?: string
  /** Maximum results to return. Defaults to 10 */
  maxResults?: number
  /** Whether the search is disabled */
  disabled?: boolean
  /** Additional class names */
  className?: string
  /** Whether to show the search input. Defaults to true */
  showInput?: boolean
  /** External query control (for controlled mode) */
  externalQuery?: string
  /** External query setter (for controlled mode) */
  onQueryChange?: (query: string) => void
  /** Send admin=true to bypass group search restrictions */
  admin?: boolean
  /** Override the grouping attribute for the gray pill on each result item.
   *  When set, passed through to DirectorySearchResultItem. */
  groupAttribute?: string
  /** Scope user results to transitive members of this Azure AD group. */
  scopeGroup?: string
  /** Drop guests and disabled accounts from the user results. */
  membersOnly?: boolean
}

/**
 * Reusable directory search component for finding Azure AD users and groups.
 * Can be used in controlled or uncontrolled mode.
 * Automatically adapts to groupSearchEnabled config setting.
 */
export function DirectorySearch({
  onSelect,
  selectedOids = [],
  types,
  placeholder,
  maxResults = 10,
  disabled = false,
  className,
  showInput = true,
  externalQuery,
  onQueryChange,
  admin,
  groupAttribute,
  scopeGroup,
  membersOnly,
}: DirectorySearchProps) {
  const { t } = useTranslation()

  // Check if group search is enabled from config
  const groupSearchEnabled = SettingsService.getInstance().settings.groupSearchEnabled ?? false

  // Determine effective search types based on config and props
  const effectiveTypes = useMemo(() => {
    if (types) {
      // Explicit types are always respected (caller knows what they need)
      return types
    }
    // Default: include groups only if enabled in config
    return groupSearchEnabled ? ["users", "groups"] : ["users"]
  }, [types, groupSearchEnabled]) as ("users" | "groups")[]

  // Adapt placeholder based on whether groups are searchable
  const defaultPlaceholder = groupSearchEnabled
    ? t("sharing.search_placeholder", { defaultValue: "Search users or groups..." })
    : t("sharing.search_users_placeholder", { defaultValue: "Search users..." })

  const { query, setQuery, results, isSearching, error, clear } = useDirectorySearch({
    types: effectiveTypes,
    top: maxResults,
    enabled: !disabled,
    scopeGroup,
    membersOnly,
    admin,
  })

  // Use external query if provided (controlled mode)
  const currentQuery = externalQuery !== undefined ? externalQuery : query
  const handleQueryChange = useCallback(
    (value: string) => {
      if (onQueryChange) {
        onQueryChange(value)
      }
      setQuery(value)
    },
    [onQueryChange, setQuery]
  )

  const handleClear = useCallback(() => {
    handleQueryChange("")
    clear()
  }, [handleQueryChange, clear])

  const handleSelect = useCallback(
    (result: DirectorySearchResult) => {
      onSelect(result)
      // Clear search after selection
      handleClear()
    },
    [onSelect, handleClear]
  )

  const selectedSet = new Set(selectedOids)

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* Search Input */}
      {showInput && (
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            className="pr-9 pl-9"
            disabled={disabled}
            placeholder={placeholder ?? defaultPlaceholder}
            type="search"
            value={currentQuery}
            onChange={(e) => handleQueryChange(e.target.value)}
          />
          {currentQuery && (
            <button
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
              type="button"
              onClick={handleClear}
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      )}

      {/* Results */}
      {currentQuery.length >= 2 && (
        <div className="bg-card rounded-md border">
          {(() => {
            if (isSearching) {
              return (
                <div className="flex items-center justify-center py-8">
                  <Spinner className="size-5" />
                </div>
              )
            }
            if (error) {
              return (
                <div className="text-destructive px-3 py-4 text-center text-sm">
                  {t("sharing.search_error", { defaultValue: "Failed to search directory" })}
                </div>
              )
            }
            if (results.length === 0) {
              return (
                <div className="text-muted-foreground px-3 py-4 text-center text-sm">
                  {groupSearchEnabled
                    ? t("sharing.no_results", { defaultValue: "No users or groups found" })
                    : t("sharing.no_users_found", { defaultValue: "No users found" })}
                </div>
              )
            }
            return (
              <ScrollArea className="h-[min(300px,_50dvh)]">
                <div className="p-1">
                  {results.map((result) => (
                    <DirectorySearchResultItem
                      key={result.oid}
                      groupAttribute={groupAttribute}
                      isSelected={selectedSet.has(result.oid)}
                      result={result}
                      onClick={() => handleSelect(result)}
                    />
                  ))}
                </div>
              </ScrollArea>
            )
          })()}
        </div>
      )}
    </div>
  )
}
